import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Modal,
  Paper,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { Pencil } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../contexts/UserContext.tsx";
import axiosInstance from "../Axios.jsx";
import { toast } from "sonner";

export const EditDashboardUserModal = ({ userDetails }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newUserData, setNewUserData] = useState({
    ...userDetails,
    user_fname: userDetails.user_fname,
    user_lname: userDetails.user_lname,
    view_access_list: {
      isEvaluator: userDetails.view_access_list.isEvaluator,
      isWrite: userDetails.view_access_list.isWrite,
    },
  });

  const [viewAccessList, setViewAccessList] = useState(
    JSON.parse(userDetails.view_access_list)
  );
  const [selectedDistricts, setSelectedDistricts] = useState(
    viewAccessList.district.split(",")
  );
  const accessDistricts = user.view_access_list.district.split(",").sort();
  const schoolTypes = user.view_access_list.school_types.split(",").sort();

  const [selectedSchoolTypes, setSelectedSchoolTypes] = useState(
    viewAccessList.school_types.split(",")
  );

  const [modal, setModal] = useState(false);
  const handleOpenTeacherModal = () => setModal(true);
  const handleCloseTeacherModal = () => setModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedSchoolTypeStr = selectedSchoolTypes.join(",");
    const selectedDistrictStr = selectedDistricts.join(",");

    const view_access_list = {
      school_types: selectedSchoolTypeStr,
      district: selectedDistrictStr,
      isEvaluator: viewAccessList.isEvaluator,
      isWrite: viewAccessList.isWrite,
    };
    //I don't know from where school_id is coming from. Too late at night to debug.
    const { school_id, ...restUserData } = newUserData;

    if (
      newUserData.user_fname === "" ||
      newUserData.user_lname === "" ||
      selectedDistricts.length === 0 ||
      selectedSchoolTypes.length === 0
    ) {
      toast.error("Please fill all fields");
      return;
    }

    console.log({ ...newUserData, view_access_list: viewAccessList });
    await axiosInstance
      .post("/updateDashboardUser", {
        ...restUserData,
        view_access_list: view_access_list,
        auth_token: user.auth_token,
      })
      .then(() => {
        toast.success("User updated successfully");
        window.location.reload();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user.user_type !== "admin") {
      return navigate("/");
    }
  }, [navigate]);

  return (
    <div className="h-full">
      <button
        onClick={() => {
          handleOpenTeacherModal();
        }}
        className="bg-[#4CAF50] hover:bg-green-600 text-white font-semibold py-2 px-2 rounded focus:outline-none focus:shadow-outline-green"
      >
        <Pencil />
      </button>
      <Modal
        open={modal}
        onClose={handleCloseTeacherModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper elevation={3} className="w-fit mx-auto mt-10 px-10 max-w-lg">
          <h1 className="text-center p-4 font-semibold text-2xl text-green-600">
            Edit Dashboard User
          </h1>
          <Box
            component="form"
            sx={{
              "& .MuiTextField-root": { m: 1, width: "40ch" },
            }}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col">
              <TextField
                defaultValue={userDetails.user_fname}
                onChange={(e) =>
                  setNewUserData({ ...newUserData, user_fname: e.target.value })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#4CAF50",
                      color: "#4CAF50",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#4CAF50",
                  },
                }}
                name="user_fname"
                label="First Name"
              />
              <TextField
                defaultValue={userDetails.user_lname}
                onChange={(e) =>
                  setNewUserData({ ...newUserData, user_lname: e.target.value })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#4CAF50",
                      color: "#4CAF50",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#4CAF50",
                  },
                }}
                name="user_lname"
                label="Last Name"
              />
              <div className="flex flex-col justify-center gap-2 w-[370px] mx-auto">
                <Select
                  className="form-control"
                  id="district"
                  multiple
                  sx={{ paddingX: "0px", height: "40px" }}
                  value={accessDistricts}
                  defaultValue={"Select Districts"}
                  renderValue={() => {
                    if (selectedDistricts.length === 0) {
                      return (
                        <span className="text-gray-500">Select Districts</span>
                      );
                    }
                    return selectedDistricts.join(", ");
                  }}
                >
                  {accessDistricts.map((district, index) => (
                    <MenuItem key={index} value={district}>
                      <FormControlLabel
                        className="w-full"
                        control={
                          <Checkbox
                            checked={selectedDistricts.includes(district)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDistricts([
                                  ...selectedDistricts,
                                  district,
                                ]);
                              } else {
                                setSelectedDistricts(
                                  selectedDistricts.filter(
                                    (dist) => dist !== district
                                  )
                                );
                              }
                            }}
                          />
                        }
                        label={district}
                      />
                    </MenuItem>
                  ))}
                </Select>

                {/* School types dropdown */}
                <Select
                  className="form-control"
                  id="schoolType"
                  multiple
                  sx={{ paddingX: "0px", height: "40px" }}
                  defaultValue={"Select Districts"}
                  disabled={selectedDistricts.length === 0}
                  value={schoolTypes}
                  renderValue={() => {
                    if (selectedSchoolTypes.length === 0) {
                      return (
                        <span className="text-gray-500">
                          Select School Types
                        </span>
                      );
                    }
                    return selectedSchoolTypes.join(", ");
                  }}
                >
                  {schoolTypes &&
                    schoolTypes.map((schoolType, index) => (
                      <MenuItem key={index} value={schoolType}>
                        <FormControlLabel
                          className="w-full"
                          control={
                            <Checkbox
                              checked={selectedSchoolTypes.includes(schoolType)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSchoolTypes([
                                    ...selectedSchoolTypes,
                                    schoolType,
                                  ]);
                                } else {
                                  setSelectedSchoolTypes(
                                    selectedSchoolTypes.filter(
                                      (dist) => dist !== schoolType
                                    )
                                  );
                                }
                              }}
                            />
                          }
                          label={schoolType}
                        />
                      </MenuItem>
                    ))}
                </Select>
              </div>
            </div>
            <FormControl
              component="fieldset"
              variant="standard"
              className="mt-2 ml-2"
            >
              {userDetails.view_access_list && (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={viewAccessList.isEvaluator}
                        onChange={(e) =>
                          setViewAccessList({
                            ...viewAccessList,
                            isEvaluator: e.target.checked,
                          })
                        }
                        name="isEvaluator"
                      />
                    }
                    label="isEvaluator"
                  />
                  <FormControlLabel
                    label="isWrite"
                    control={
                      <Switch
                        checked={viewAccessList.isWrite}
                        onChange={(e) =>
                          setViewAccessList({
                            ...viewAccessList,
                            isWrite: e.target.checked,
                          })
                        }
                        name="isWrite"
                      />
                    }
                  />
                </FormGroup>
              )}
            </FormControl>
            <div className="w-full flex justify-center p-10">
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#45a049",
                  },
                }}
              >
                Submit
              </Button>
            </div>
          </Box>
        </Paper>
      </Modal>
    </div>
  );
};
